import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { supabase } from './supabase'
import { getFullName } from './clientUtils'

const lsKey = (merchantId) => `messages:lastRead:${merchantId}`

const readLastReadMap = (merchantId) => {
	if (!merchantId) return {}
	try {
		const raw = localStorage.getItem(lsKey(merchantId))
		return raw ? JSON.parse(raw) : {}
	} catch {
		return {}
	}
}

const writeLastReadMap = (merchantId, map) => {
	if (!merchantId) return
	try {
		localStorage.setItem(lsKey(merchantId), JSON.stringify(map))
	} catch (e) {
		console.warn('useUnreadMessages: failed to persist lastReadMap', e)
	}
}

/**
 * Tracks unread inbound SMS messages per client for the given merchant.
 *
 * Storage model — localStorage only (frontend-only feature):
 *   key:   `messages:lastRead:<merchantId>`
 *   value: { [clientId]: ISO_lastReadAt }
 * Consequence: unread state is per-device. A merchant who reads a thread on
 * their laptop will still see the dot on their phone until they open it there.
 * Designed to map cleanly to a future server-side `read_at` column if we ever
 * want cross-device sync.
 *
 * Side effects:
 *   - Subscribes to sms_messages INSERTs for the merchant. Stays mounted as
 *     long as the dashboard is mounted so notifications/badge updates work
 *     across all tabs, not just the Messages page.
 *   - Fires a desktop Notification on inbound when the tab is hidden and
 *     permission has been granted. tag-collapses repeats for the same client.
 *   - Listens for cross-tab `storage` events so reads in one tab clear unread
 *     state in any other open tab.
 */
export function useUnreadMessages(merchant) {
	const merchantId = merchant?.id

	// Latest inbound created_at per client. Updated by the initial query and
	// every realtime INSERT.
	const [latestInboundByClient, setLatestInboundByClient] = useState({})

	// Last-read timestamp per client, mirrored to localStorage.
	const [lastReadMap, setLastReadMap] = useState(() => readLastReadMap(merchantId))

	useEffect(() => {
		setLastReadMap(readLastReadMap(merchantId))
	}, [merchantId])

	// Cross-tab sync: another tab writing the same key updates our state too.
	useEffect(() => {
		if (!merchantId) return undefined
		const key = lsKey(merchantId)
		const onStorage = (e) => {
			if (e.key !== key) return
			try {
				setLastReadMap(e.newValue ? JSON.parse(e.newValue) : {})
			} catch {
				/* ignore */
			}
		}
		window.addEventListener('storage', onStorage)
		return () => window.removeEventListener('storage', onStorage)
	}, [merchantId])

	// Initial fetch: every inbound for the merchant, reduced to latest per client.
	useEffect(() => {
		if (!merchantId) return undefined
		let cancelled = false
		;(async () => {
			const { data, error } = await supabase
				.from('sms_messages')
				.select('client_id, created_at')
				.eq('merchant_id', merchantId)
				.eq('direction', 'inbound')
				.order('created_at', { ascending: false })

			if (cancelled) return
			if (error) {
				console.error('useUnreadMessages: initial fetch failed', error)
				return
			}

			const latest = {}
			for (const row of data || []) {
				if (!row.client_id) continue
				if (!latest[row.client_id]) latest[row.client_id] = row.created_at
			}
			setLatestInboundByClient(latest)

			// First-time-on-this-device seeding: if there's no lastReadMap yet,
			// mark every existing inbound as already-read so we don't blast the
			// merchant with N unread dots the first time this feature ships.
			const existing = readLastReadMap(merchantId)
			if (Object.keys(existing).length === 0 && Object.keys(latest).length > 0) {
				writeLastReadMap(merchantId, latest)
				setLastReadMap(latest)
			}
		})()
		return () => {
			cancelled = true
		}
	}, [merchantId])

	// Realtime subscription. One always-on channel per merchant; lives for the
	// dashboard's lifetime so the sidebar badge & notifications keep updating
	// even when the merchant is on Calendar / Settings / etc.
	useEffect(() => {
		if (!merchantId) return undefined
		const channel = supabase
			.channel(`unread-sms:${merchantId}`)
			.on(
				'postgres_changes',
				{
					event: 'INSERT',
					schema: 'public',
					table: 'sms_messages',
					filter: `merchant_id=eq.${merchantId}`,
				},
				(payload) => {
					const msg = payload.new
					if (!msg || msg.direction !== 'inbound' || !msg.client_id) return

					setLatestInboundByClient((prev) => {
						const prevTs = prev[msg.client_id]
						if (prevTs && new Date(prevTs) >= new Date(msg.created_at)) return prev
						return { ...prev, [msg.client_id]: msg.created_at }
					})

					// Desktop notification on every inbound. Browser/OS settings
					// still control whether it actually surfaces as an OS toast.
					// We look up the client name so the title reads as
					// "<Client Name>" with the message body underneath — the
					// realtime payload only includes client_id.
					if (
						typeof Notification !== 'undefined' &&
						Notification.permission === 'granted'
					) {
						;(async () => {
							let title = 'New message'
							try {
								const { data: client } = await supabase
									.from('clients')
									.select('name, last_name')
									.eq('id', msg.client_id)
									.single()
								const fullName = getFullName(client)
								if (fullName) title = fullName
							} catch {
								/* fall back to default title */
							}
							try {
								// Keep options minimal — some browsers silently drop
								// the notification when given options they consider
								// invalid (e.g. an `icon` URL that 404s).
								const n = new Notification(title, {
									body: msg.message_text || '',
									tag: `sms-${msg.client_id}`,
								})
								n.onclick = (event) => {
									// Spec recommends preventDefault — without it some
									// browsers (notably Chrome) handle the click by
									// opening a fresh tab on the origin (`/`), which
									// our router then redirects to `/home`.
									if (event && typeof event.preventDefault === 'function') {
										event.preventDefault()
									}
									try {
										window.focus()
									} catch {/* ignore */}
									// Defer the navigation a tick so it doesn't race with
									// the browser's default focus-handling. Use the full
									// origin URL so we always land on the right path even
									// if the click spawned a new tab.
									const target = `${window.location.origin}/dashboard?tab=messages`
									setTimeout(() => {
										window.location.href = target
									}, 0)
									n.close()
								}
							} catch (e) {
								console.warn('useUnreadMessages: Notification failed', e)
							}
						})()
					}
				}
			)
			.subscribe()

		return () => {
			supabase.removeChannel(channel)
		}
	}, [merchantId])

	// Mark a single client's thread as read up to now. Stored in localStorage
	// and mirrored to React state. Idempotent — safe to call multiple times.
	const markRead = useCallback(
		(clientId) => {
			if (!merchantId || !clientId) return
			const nowIso = new Date().toISOString()
			setLastReadMap((prev) => {
				const next = { ...prev, [clientId]: nowIso }
				writeLastReadMap(merchantId, next)
				return next
			})
		},
		[merchantId]
	)

	// Derive { [clientId]: true } for clients with at least one unread inbound,
	// plus a total count for the sidebar badge. Memoised so consumers that
	// depend on these don't re-render every keystroke.
	const { unreadByClient, totalUnread } = useMemo(() => {
		const byClient = {}
		let total = 0
		for (const [cid, ts] of Object.entries(latestInboundByClient)) {
			const readAt = lastReadMap[cid]
			if (!readAt || new Date(ts) > new Date(readAt)) {
				byClient[cid] = true
				total += 1
			}
		}
		return { unreadByClient: byClient, totalUnread: total }
	}, [latestInboundByClient, lastReadMap])

	// Stable ref so handlers can read latest without re-binding.
	const markReadRef = useRef(markRead)
	useEffect(() => {
		markReadRef.current = markRead
	}, [markRead])

	return { unreadByClient, totalUnread, markRead, markReadRef, lastReadMap }
}
